// -------------------------------
// DESIGNATION INTERFACES
// -------------------------------
export interface PendingDesignation {
    name?: string;
    department?: string;
    description?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteDesignation {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE DESIGNATION — START
// -------------------------------
export const startCreateDesignationFlow = (
    setPendingDesignation: (data: PendingDesignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingDesignation({ name: nameFromIntent });

        pushAIChat(`Great! Which department does "${nameFromIntent}" belong to?`);
        speak(`Which department does ${nameFromIntent} belong to?`);
        return;
    }

    setPendingDesignation({});
    pushAIChat("Sure! What should the designation be called?");
    speak("What should the designation be called?");
};


// -------------------------------
// CREATE DESIGNATION — PROCESS
// -------------------------------
export const processPendingDesignation = (
    text: string,
    pending: PendingDesignation,
    setPendingDesignation: (data: PendingDesignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Name
    if (!pending.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid designation name.");
            speak("Please provide a valid designation name.");
            return;
        }

        setPendingDesignation({ ...pending, name: cleanName });

        pushAIChat(`Which department does "${cleanName}" belong to?`);
        speak(`Which department does ${cleanName} belong to?`);
        return;
    }

    // STEP 2 — Department
    if (!pending.department) {
        const department = text.trim();

        if (!department) {
            pushAIChat("Please provide a valid department name.");
            speak("Please provide a valid department name.");
            return;
        }

        setPendingDesignation({ ...pending, department });

        pushAIChat("Any description for this designation?");
        speak("Any description?");
        return;
    }

    // STEP 3 — Description
    if (!pending.description) {
        setPendingDesignation({ ...pending, description: text });

        pushAIChat("Should this designation be active or inactive?");
        speak("Should this designation be active or inactive?");
        return;
    }

    // STEP 4 — Status
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive") ? "inactive" : "active";

        setPendingDesignation({ ...pending, status });

        // FINAL — CREATE DESIGNATION
        pushAIChat(`Perfect! Creating designation "${pending.name}"...`);
        speak(`Creating designation ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-designation", {
                detail: {
                    ...pending,
                    status
                }
            })
        );

        setPendingDesignation(null);
        return;
    }
};


// -------------------------------
// DELETE DESIGNATION — START
// -------------------------------
export const startDeleteDesignationFlow = (
    setPendingDelete: (data: PendingDeleteDesignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting designation "${nameFromIntent}"...`);
        speak(`Deleting designation ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-designation", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which designation would you like to delete?");
    speak("Which designation would you like to delete?");
};


// -------------------------------
// DELETE DESIGNATION — PROCESS
// -------------------------------
export const processPendingDeleteDesignation = (
    text: string,
    pending: PendingDeleteDesignation,
    setPendingDelete: (data: PendingDeleteDesignation | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid designation name.");
            speak("Please provide a valid designation name.");
            return;
        }

        pushAIChat(`Deleting designation "${cleanName}"...`);
        speak(`Deleting designation ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-designation", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
    }
};
